/**
 * Từ vựng nghiệp vụ SCR-025 (S11.6, khuôn `nccCategoryVocabulary`): nhãn + sắc thái
 * cho mã `identityStatus` theo CR-S11-2-001 D5. UI presentation-only — mã lạ LUÔN
 * hiển thị nguyên văn với tone neutral, không tự suy diễn trạng thái hồ sơ.
 */
import { label } from "../../lib/labels";

export type IdentityStatusTone = "success" | "warning" | "info" | "neutral";

export const IDENTITY_STATUS_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: IdentityStatusTone }>>
> = {
  NO_GRAVE_INFO: { label: "Chưa có thông tin mộ", tone: "neutral" },
  GRAVE_RECORDED: { label: "Đã có mộ", tone: "success" },
  GRAVE_INFO_INCOMPLETE: { label: "Mộ thiếu thông tin", tone: "warning" },
  UNDER_VERIFICATION: { label: "Đang xác minh", tone: "info" },
  IDENTITY_CONFIRMED: { label: "Đã xác định danh tính", tone: "success" },
};

/** Nhãn nghiệp vụ của trạng thái danh tính; mã chưa có trong từ vựng trả nguyên văn. */
export function identityStatusLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return IDENTITY_STATUS_LABELS[code]?.label ?? label(code, displayName);
}

/** Sắc thái của trạng thái danh tính; mã lạ luôn neutral (không tô đậm giả định). */
export function identityStatusTone(code: string): IdentityStatusTone {
  return IDENTITY_STATUS_LABELS[code]?.tone ?? "neutral";
}

export const PUBLIC_STATUS_LABELS: Readonly<Record<string, string>> = {
  PUBLISHED: "Đã công khai",
  RESTRICTED: "Hạn chế",
  PUBLIC_APPROVED: "Đã duyệt công khai",
};

/** Nhãn nghiệp vụ cho trạng thái công khai hồ sơ liệt sĩ. */
export function publicStatusLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return PUBLIC_STATUS_LABELS[code] ?? label(code, displayName);
}

export {
  LINK_TYPE_LABELS,
  linkTypeLabel,
  linkTypeTone,
  CONFIDENCE_STATUS_LABELS,
  confidenceStatusLabel,
} from "../graves/graveVocabulary";

export const RELATIONSHIP_TYPE_LABELS: Readonly<Record<string, string>> = {
  CHA: "Cha",
  ME: "Mẹ",
  VO_CHONG: "Vợ/Chồng",
  CON: "Con",
  CON_DE: "Con đẻ",
  CON_NUOI: "Con nuôi",
  NGUOI_NUOI: "Người nuôi dưỡng",
  NGUOI_HUONG: "Người hưởng",
  DAI_DIEN: "Người đại diện",
  UY_QUYEN: "Người được ủy quyền",
  THO_CUNG: "Người thờ cúng",
  NGUOI_PHUC_VU: "Người phục vụ",
  ANH_CHI_EM: "Anh/Chị/Em",
};

export function relationshipTypeLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  const normalized = code ? code.trim().toUpperCase() : "";
  return RELATIONSHIP_TYPE_LABELS[normalized] ?? label(code, displayName);
}

export const DOCUMENT_ROLE_LABELS: Readonly<Record<string, string>> = {
  FATHERLAND_MERIT: "Bằng Tổ quốc ghi công",
  DEATH_NOTICE: "Giấy báo tử",
  DECISION: "Quyết định công nhận",
  EVIDENCE: "Tài liệu bằng chứng",
};

export function documentRoleLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  const normalized = code ? code.trim().toUpperCase() : "";
  return DOCUMENT_ROLE_LABELS[normalized] ?? label(code, displayName);
}

export const DOCUMENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  BANG_TQGC: "Bằng Tổ quốc ghi công",
  GIAY_BAO_TU: "Giấy báo tử",
  QUYET_DINH: "Quyết định",
  TRICH_LUC: "Trích lục",
  HO_SO: "Hồ sơ",
  GIAY_TO: "Giấy tờ liên quan",
  SCAN: "Bản số hóa",
};

export function documentTypeLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  const normalized = code ? code.trim().toUpperCase() : "";
  return DOCUMENT_TYPE_LABELS[normalized] ?? label(code, displayName);
}

